import { useEffect, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from 'lucide-react';

import { useDebounce } from '@/react/hooks/useDebounce';

import { Widget } from '@@/Widget';
import { Button } from '@@/buttons';
import { InlineLoader } from '@@/InlineLoader';
import { Alert } from '@@/Alert';
import { ExternalLink } from '@@/ExternalLink';
import { DateRangeTimePicker } from '@@/DateRangeTimePicker/DateRangeTimePicker';

import { DetailLogsGetFn, LogEntry } from './types';
import { LogDetailModal } from './LogDetailModal';
import { LogTableRow } from './LogTableRow';
import { SEVERITY_LEVELS } from './SeverityPill';
import { useDetailLogsQuery, PAGE_SIZE } from './useDetailLogsQuery';
import { toTitleCase } from './utils';

interface Props {
  queryKey: readonly unknown[];
  getLogsFn: DetailLogsGetFn;
  projectUrl?: string;
  defaultSince: number;
  defaultUntil: number;
}

// Heights in px for manual FixedSizeList height calculation
const CONTROLS_HEIGHT = 79; // py-2 (~32px) + my-4 (32px top+bottom) + border
const HEADER_HEIGHT = 33;
const ROW_HEIGHT = 33;
const PAGINATION_HEIGHT = 47;

export function DetailLogsViewer({
  queryKey,
  getLogsFn,
  projectUrl = '',
  defaultSince,
  defaultUntil,
}: Props) {
  const [page, setPage] = useState(0);
  const [levelFilter, setLevelFilter] = useState('');
  const [since, setSince] = useState(defaultSince);
  const [until, setUntil] = useState(defaultUntil);
  // LogsSearchBar debounces before invoking handleSearchChange, so `search` is
  // already debounced when it lands in the query key.
  const [search, setSearch] = useState('');
  const listRef = useRef<FixedSizeList>(null);
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(0);
  }

  const severities = levelFilter ? [levelFilter] : [];

  const { data, isFetching, isError } = useDetailLogsQuery({
    queryKey,
    getLogsFn,
    page,
    severities,
    since,
    until,
    search,
  });

  const logs = data?.logs ?? [];
  const hasNextPage = logs.length >= PAGE_SIZE;

  useEffect(() => {
    listRef.current?.scrollToItem(0, 'start');
  }, [page]);

  return (
    <div className="h-full">
      <Widget className="flex h-[calc(100%-20px)] flex-col">
        <Widget.Body className="no-padding flex-auto" contentClassName="h-full">
          <AutoSizer defaultHeight={750} disableWidth className="h-full">
            {({ height }) => (
              <div className="flex flex-col" style={{ height }}>
                {/* Controls: search + level + date range */}
                <div className="my-4 flex items-center gap-2 border-b border-gray-5 px-4 py-2 th-highcontrast:border-white th-dark:border-gray-7">
                  <LogsSearchBar value={search} onChange={handleSearchChange} />

                  <div className="searchBar flex shrink-0 items-center gap-1.5">
                    <select
                      value={levelFilter}
                      onChange={(e) => {
                        setLevelFilter(e.target.value);
                        setPage(0);
                      }}
                      className="cursor-pointer appearance-none border-0 bg-transparent text-sm font-normal outline-none"
                      data-cy="observability-level-filter"
                      aria-label="Severity level"
                    >
                      <option value="">All levels</option>
                      {SEVERITY_LEVELS.map((sev) => (
                        <option key={sev} value={sev}>
                          {toTitleCase(sev)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="searchIcon shrink-0" />
                  </div>

                  <DateRangeTimePicker
                    since={since}
                    until={until}
                    onSinceChange={(v) => {
                      setSince(v);
                      setPage(0);
                    }}
                    onUntilChange={(v) => {
                      setUntil(v);
                      setPage(0);
                    }}
                  />

                  {projectUrl && (
                    <ExternalLink
                      to={projectUrl}
                      data-cy="observability-open-oneuptime"
                      className="ml-1 shrink-0 items-center gap-1 text-xs text-blue-8 hover:text-blue-9 th-highcontrast:text-blue-4 th-highcontrast:hover:text-blue-3 th-dark:text-blue-4 th-dark:hover:text-blue-3"
                    >
                      Open in OneUptime
                    </ExternalLink>
                  )}
                </div>

                {/* Column headers */}
                <div className="grid grid-cols-[200px_140px_100px_1fr] gap-x-3 border-b border-gray-3 bg-gray-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-7 th-highcontrast:border-white th-highcontrast:bg-black th-highcontrast:text-white th-dark:border-gray-8 th-dark:bg-gray-iron-11 th-dark:text-gray-4">
                  <span>Time</span>
                  <span>Source</span>
                  <span>Level</span>
                  <span>Message</span>
                </div>

                {isFetching && !data ? (
                  <div
                    style={{ height: listHeight(height) }}
                    className="flex items-center justify-center"
                  >
                    <InlineLoader>Loading logs...</InlineLoader>
                  </div>
                ) : isError ? (
                  <div
                    style={{ height: listHeight(height) }}
                    className="flex items-center justify-center px-4"
                  >
                    <Alert color="error" title="Unable to load logs">
                      Something went wrong fetching logs for the selected time
                      range and filters. Try again, or adjust your filters.
                    </Alert>
                  </div>
                ) : logs.length === 0 ? (
                  <div
                    style={{ height: listHeight(height) }}
                    className="flex items-center justify-center text-sm text-gray-6 th-highcontrast:text-gray-5 th-dark:text-gray-5"
                  >
                    No logs found for the selected time range and filters.
                  </div>
                ) : (
                  <FixedSizeList
                    ref={listRef}
                    height={listHeight(height)}
                    width="100%"
                    itemCount={logs.length}
                    itemSize={ROW_HEIGHT}
                    overscanCount={5}
                  >
                    {({ index, style }) => (
                      <LogTableRow
                        log={logs[index]}
                        style={style}
                        onSelect={setSelectedLog}
                      />
                    )}
                  </FixedSizeList>
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-gray-5 px-4 py-2 th-highcontrast:border-white th-dark:border-gray-7">
                  <Button
                    color="default"
                    size="small"
                    icon={ChevronLeft}
                    disabled={page === 0 || isFetching}
                    onClick={() => setPage((p) => p - 1)}
                    data-cy="observability-logs-prev-page"
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-gray-7 th-highcontrast:text-white th-dark:text-gray-4">
                    Page {page + 1}
                  </span>
                  <Button
                    color="default"
                    size="small"
                    icon={ChevronRight}
                    disabled={!hasNextPage || isFetching}
                    onClick={() => setPage((p) => p + 1)}
                    data-cy="observability-logs-next-page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </AutoSizer>
        </Widget.Body>
      </Widget>

      {selectedLog && (
        <LogDetailModal
          log={selectedLog}
          onDismiss={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
}

// LogsSearchBar renders the log search field. Unlike the datatable SearchBar it
// stretches the input to fill the toolbar so the clear button sits flush right,
// and it only renders that button while the field has a value.
function LogsSearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  // The debounced handler fires onChange with the debounced value, so `search`
  // is already debounced by the time it lands in the query key.
  const [searchValue, setSearchValue] = useDebounce(value, onChange);

  return (
    <div className="searchBar flex flex-1 items-center">
      <Search className="searchIcon lucide shrink-0" />
      <input
        type="text"
        className="searchInput flex-1"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder="Search logs"
        aria-label="Search logs"
        data-cy="observability-logs-search"
      />
      {!!searchValue && (
        <Button
          onClick={() => setSearchValue('')}
          icon={X}
          color="none"
          aria-label="Clear search"
          data-cy="observability-logs-search-clear-button"
        />
      )}
    </div>
  );
}

function listHeight(containerHeight: number): number {
  return Math.max(
    1,
    containerHeight - CONTROLS_HEIGHT - HEADER_HEIGHT - PAGINATION_HEIGHT
  );
}
